/**
 * The Ledger primitives and form controls, as Chakra recipes.
 *
 * Every raw Chakra `<Button>`, `<Input>`, `<Checkbox>` … in the app reads these,
 * so registering them here restyles each existing use at once; the wrappers in
 * `src/components/ui/` add the Ledger behaviour (busy, disabled reasons, hit
 * areas, field wiring) on top. Merged after `ledgerTheme.ts` in
 * `src/pages/_app.tsx`.
 *
 * Step 5 adds the data display (table, alert as the Banner, empty state,
 * card, code, code block, stat), from `./recipes/data.ts`.
 *
 * Step 4 adds the layers (dialog, drawer as the Ledger Sheet, popover, hover
 * card, menu, toast, tabs) and the layer manager's stacking: the band
 * properties on `:root` and Chakra's layer `zIndex` keys, both from
 * `./layerStack.ts`.
 *
 * Spec: specs/design-system/ledger-primitives.feature,
 * specs/design-system/ledger-layers.feature,
 * specs/design-system/ledger-data-display.feature
 */
import { defineConfig, defineRecipe } from "@chakra-ui/react";
import {
  layerExitCss,
  layerStackProperties,
  layerZIndexTokens,
} from "./layerStack";
import { buttonRecipe } from "./recipes/button";
import { badgeRecipe, tagSlotRecipe } from "./recipes/chip";
import {
  alertSlotRecipe,
  cardSlotRecipe,
  codeBlockSlotRecipe,
  codeRecipe,
  emptyStateSlotRecipe,
  statSlotRecipe,
  tableSlotRecipe,
} from "./recipes/data";
import { drawerSlotRecipe } from "./recipes/drawer";
import {
  avatarSlotRecipe,
  kbdRecipe,
  ledgerKeyframes,
  separatorRecipe,
  skeletonRecipe,
  spinnerRecipe,
  tooltipSlotRecipe,
} from "./recipes/feedback";
import {
  checkboxSlotRecipe,
  comboboxSlotRecipe,
  fieldSlotRecipe,
  inputRecipe,
  nativeSelectSlotRecipe,
  radioGroupSlotRecipe,
  radiomarkRecipe,
  segmentGroupSlotRecipe,
  selectSlotRecipe,
  switchSlotRecipe,
  textareaRecipe,
} from "./recipes/forms";
import {
  dialogSlotRecipe,
  hoverCardSlotRecipe,
  menuSlotRecipe,
  popoverSlotRecipe,
  tabsSlotRecipe,
  toastSlotRecipe,
} from "./recipes/layers";

/**
 * The heading's `xl` size is the Ledger page title: `--text-18` semibold at
 * the tight leading, the title in every page's 40px header row
 * (`PageLayout.Heading`). Merged into the app's heading recipe, so its other
 * sizes keep their values.
 */
const headingRecipe = defineRecipe({
  variants: {
    size: {
      xl: {
        fontSize: "var(--text-18)",
        fontWeight: "var(--weight-semibold)",
        lineHeight: "var(--leading-tight)",
      },
    },
  },
});

export const ledgerRecipesConfig = defineConfig({
  globalCss: {
    ":root": layerStackProperties,
    ...layerExitCss,
  },
  theme: {
    tokens: {
      zIndex: layerZIndexTokens,
    },
    keyframes: ledgerKeyframes,
    recipes: {
      button: buttonRecipe,
      badge: badgeRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
      radiomark: radiomarkRecipe,
      separator: separatorRecipe,
      skeleton: skeletonRecipe,
      spinner: spinnerRecipe,
      kbd: kbdRecipe,
      code: codeRecipe,
      heading: headingRecipe,
    },
    slotRecipes: {
      tag: tagSlotRecipe,
      checkbox: checkboxSlotRecipe,
      switch: switchSlotRecipe,
      radioGroup: radioGroupSlotRecipe,
      nativeSelect: nativeSelectSlotRecipe,
      select: selectSlotRecipe,
      combobox: comboboxSlotRecipe,
      segmentGroup: segmentGroupSlotRecipe,
      field: fieldSlotRecipe,
      tooltip: tooltipSlotRecipe,
      avatar: avatarSlotRecipe,
      dialog: dialogSlotRecipe,
      drawer: drawerSlotRecipe,
      popover: popoverSlotRecipe,
      hoverCard: hoverCardSlotRecipe,
      menu: menuSlotRecipe,
      toast: toastSlotRecipe,
      tabs: tabsSlotRecipe,
      table: tableSlotRecipe,
      alert: alertSlotRecipe,
      emptyState: emptyStateSlotRecipe,
      card: cardSlotRecipe,
      codeBlock: codeBlockSlotRecipe,
      stat: statSlotRecipe,
    },
  },
});
